import { categoryOf } from "../../utils/regulatoryCategories"
import { memo } from "react"
import { Collapse, Tag } from "antd"
import { ICloudRegulatoryControlPoint } from "../../types"
import { RegulatoryControlAnswer } from "../../generated/proto-ts/main"
import { answerFields, answerStatus, severityLabels } from "../../utils/regulatoryAnswers"
import { controlGroup, regulatoryUuid } from "../../utils/regulatoryPresentation"
import "./regulatoryCards.css"
export function ControlHeading({
  point,
  answer,
}: {
  point: ICloudRegulatoryControlPoint
  answer?: RegulatoryControlAnswer
}) {
  const status = answerStatus(answer?.status)
  return (
    <header className="reg-control-heading">
      <div>
        {point.ContextLabel && (
          <div className="reg-control-context">
            <strong>{point.ContextLabel}</strong>
          </div>
        )}
        {point.SharedWith?.length ? (
          <small>Constat commun · utilisé par : {point.SharedWith.join(" ; ")}</small>
        ) : null}
        {point.SourceAssignments && point.SourceAssignments.length > 1 && (
          <small>Sources : {point.SourceAssignments.join(" ; ")}</small>
        )}
        {point.ClassificationConflict && (
          <Tag color="orange">
            Classements différents selon les sources · premier classement affiché
          </Tag>
        )}
        <div className="reg-control-subtitle">
          {controlGroup(point)} · {point.ID}
        </div>
        <h3>
          <img
            className="reg-category-icon"
            src={`/icones/${categoryOf(point).iconKey}.png`}
            alt=""
          />{" "}
          {point.Text}
        </h3>
        <span className="reg-control-subtitle">{categoryOf(point).label}</span>
        {answer && !regulatoryUuid(answer.control_point_revision_uuid) && (
          <Tag color="orange">Réponse historique : révision inconnue</Tag>
        )}
        <div className="reg-control-reference">
          {point.Definition?.reference} {point.ReferenceSection}
        </div>
      </div>
      <Tag color={status.color}>{status.label}</Tag>
    </header>
  )
}
export const ControlDefinition = memo(function ControlDefinition({
  point,
  print = false,
}: {
  point: ICloudRegulatoryControlPoint
  print?: boolean
}) {
  const p = point.Definition
  if (!p) return <p className="reg-control-subtitle">Définition historique inconnue.</p>
  const content = (
    <div className="reg-control-definition">
      {p.nature && (
        <p>
          <strong>Nature :</strong> {p.nature}
        </p>
      )}
      {p.applicability && (
        <p>
          <strong>Application :</strong> {p.applicability}
        </p>
      )}
      {p.method && (
        <p>
          <strong>Méthode :</strong> {p.method}
        </p>
      )}
      {p.expected && (
        <p>
          <strong>Critère attendu :</strong> {p.expected}
        </p>
      )}
      {p.unit && (
        <p>
          <strong>Unité(s) :</strong> {p.unit}
        </p>
      )}
      {(p.photoRequired || p.measurementRequired || p.evidenceRequired) && (
        <p>
          <strong>Éléments attendus :</strong>{" "}
          {[
            p.measurementRequired && "mesure",
            p.photoRequired && "photo",
            p.evidenceRequired && "justificatif",
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
      {(point.Source?.pdfPages?.length || point.Source?.pageLabel || p.sourceFile) && (
        <p className="reg-control-subtitle">
          Source : {p.sourceFile} {point.Source?.pageLabel && ` · p. ${point.Source.pageLabel}`}{" "}
          {point.Source?.pdfPages?.length ? ` · PDF ${point.Source.pdfPages.join(", ")}` : ""}
        </p>
      )}
      {(p.coverageGap || p.interpretationReserve || p.missingPart || p.interpretationNote) && (
        <p className="reg-control-notice">
          {p.missingPart || "Limites de couverture / interprétation à examiner"}{" "}
          {p.interpretationNote}
        </p>
      )}
    </div>
  )
  return print ? (
    content
  ) : (
    <Collapse
      size="small"
      items={[{ key: "definition", label: "Méthode, critères et références", children: content }]}
    />
  )
})
export function RecordedFinding({ answer }: { answer?: RegulatoryControlAnswer }) {
  return (
    <div className="reg-recorded-finding">
      <p>
        <strong>Constat :</strong> {answer?.comment || "Non renseigné"}
      </p>
      {answerFields.map((f) =>
        answer?.[f.key] ? (
          <p key={f.key}>
            <strong>{f.label} :</strong> {answer[f.key]}
          </p>
        ) : null,
      )}
      {answer?.severity && (
        <p>
          <strong>Gravité interne :</strong> {severityLabels[answer.severity] ?? answer.severity}
        </p>
      )}
      {answer?.due_date && (
        <p>
          <strong>Échéance proposée :</strong> {answer.due_date.split("-").reverse().join("/")}
        </p>
      )}
      {!!answer?.pictures?.length && (
        <p>
          <strong>Preuves :</strong> {answer.pictures.length} photo(s), voir annexes.
        </p>
      )}
    </div>
  )
}
